/**
 * Composable for certificate download functionality
 * Can be used in both client and admin contexts
 */

import { generateAccreditationCertificate } from '../scripts/certificate-generator.js';
import { ref } from 'vue';

// PDF Template Paths
const CERTIFICATE_TEMPLATE_PATH = '/dist/pdf/cert_template_simple.pdf';
const CERTIFICATE_TEMPLATE_GHOST_PATH = '/dist/pdf/cert_ghost_template_simple.pdf';

// Localized Script Keys
const REST_URL_KEY = 'resturl';
const REST_NONCE_KEY = 'restnonce';
const ADMIN_REST_URL_KEY = 'adminresturl';
const ADMIN_REST_NONCE_KEY = 'adminrestnonce';
const TEMPLATE_DIR_KEY = 'templatedir';

export function useCertificateDownload() {
    const isDownloading = ref(false);
    const downloadError = ref(null);

    /**
     * Download a certificate for a facility
     *
     * @param {number} facilityId - The facility ID
     * @param {boolean} useGhostTemplate - Whether to use the ghost template (default: false)
     * @returns {Promise<void>}
     */
    const downloadCertificate = async (facilityId, useGhostTemplate = false) => {
        if (!facilityId) {
            const error = 'Facility ID is required';
            if (typeof toastr !== 'undefined') {
                toastr.error(error, 'Error');
            } else {
                alert(error);
            }
            return;
        }

        isDownloading.value = true;
        downloadError.value = null;

        try {
            // Determine which template to use
            const templateUrl = useGhostTemplate
                ? localized[TEMPLATE_DIR_KEY] + CERTIFICATE_TEMPLATE_GHOST_PATH
                : null; // null will use the default template

            // Resolve the best available REST URL and nonce for the current context.
            // Admin provides adminresturl/adminrestnonce; client provides resturl/restnonce.
            const restUrl = localized[ADMIN_REST_URL_KEY] || localized[REST_URL_KEY];
            const restNonce = localized[ADMIN_REST_NONCE_KEY] || localized[REST_NONCE_KEY];

            // Generate and download the certificate
            await generateAccreditationCertificate(
                facilityId,
                restUrl,
                restNonce,
                templateUrl
            );

            // Show success message
            if (typeof toastr !== 'undefined') {
                toastr.success('Certificate downloaded successfully', 'Success');
            }

        } catch (error) {
            console.error('Error downloading certificate:', error);
            downloadError.value = error.message || 'Failed to download certificate';

            // Error handling is also done in generateAccreditationCertificate
            // but we set it here for reactive state
        } finally {
            isDownloading.value = false;
        }
    };

    return {
        isDownloading,
        downloadError,
        downloadCertificate
    };
}
